import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { T, BRAND } from "@/content/site";
import { scrollToId } from "@/hooks/useLenis";

const links = [
  { key: "story", id: "philosophy" },
  { key: "collection", id: "collection" },
  { key: "privateLabel", id: "private-label" },
  { key: "process", id: "process" },
  { key: "gallery", id: "gallery" },
  { key: "contact", id: "contact" },
];

export default function Nav() {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), 60);
  };

  return (
    <>
      <motion.header
        data-testid="site-nav"
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1.2, delay: 0.2, ease: [0.19, 1, 0.22, 1] }}
        className="fixed top-0 left-0 right-0 z-40"
      >
        <div
          className={
            "transition-all duration-700 border-b " +
            (scrolled
              ? "bg-[#0F0F0F]/85 backdrop-blur-xl border-white/10"
              : "bg-transparent border-transparent")
          }
        >
          <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex items-center justify-between h-[76px]">
            <button
              data-testid="nav-logo"
              onClick={() => handleNav("hero")}
              className="flex items-center h-full"
              aria-label="SACRVM home"
            >
              <img
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_mezcal-luxury/artifacts/7kzlr9vd_LOGO%20SCRVM%20BLANCO.webp"
                alt="Mezcal SACRVM"
                className="h-[42px] md:h-[52px] w-auto object-contain"
                loading="eager"
              />
            </button>

            <nav className="hidden md:flex items-center gap-10">
              {links.map((l) => (
                <button
                  key={l.key}
                  data-testid={`nav-link-${l.key}`}
                  onClick={() => handleNav(l.id)}
                  className="text-[11px] tracking-[0.28em] uppercase text-white/80 hover:text-[color:var(--sacrvm-gold)] transition-colors duration-500"
                >
                  {t(T.nav[l.key])}
                </button>
              ))}
            </nav>

            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-1 text-[11px] tracking-[0.28em] uppercase" data-testid="lang-toggle">
                <button
                  data-testid="lang-es"
                  onClick={() => setLang("es")}
                  className={"px-2 py-1 transition-colors " + (lang === "es" ? "text-[color:var(--sacrvm-gold)]" : "text-white/50 hover:text-white")}
                >
                  ES
                </button>
                <span className="text-white/20">/</span>
                <button
                  data-testid="lang-en"
                  onClick={() => setLang("en")}
                  className={"px-2 py-1 transition-colors " + (lang === "en" ? "text-[color:var(--sacrvm-gold)]" : "text-white/50 hover:text-white")}
                >
                  EN
                </button>
              </div>
              <button
                data-testid="mobile-menu-btn"
                onClick={() => setOpen((v) => !v)}
                className="md:hidden text-white p-2"
                aria-label="Menu"
              >
                {open ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="fixed inset-0 z-30 bg-[#0F0F0F]/95 backdrop-blur-2xl md:hidden pt-[76px]"
          >
            <div className="px-6 py-10 flex flex-col gap-6">
              {links.map((l, i) => (
                <motion.button
                  key={l.key}
                  data-testid={`mobile-nav-link-${l.key}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.6 }}
                  onClick={() => handleNav(l.id)}
                  className="text-left font-serif text-3xl text-white"
                >
                  {t(T.nav[l.key])}
                </motion.button>
              ))}
              <div className="hair mt-4" />
              <div className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase">
                <button data-testid="lang-es-mobile" onClick={() => setLang("es")} className={lang === "es" ? "text-[color:var(--sacrvm-gold)]" : "text-white/50"}>ES</button>
                <span className="text-white/20">/</span>
                <button data-testid="lang-en-mobile" onClick={() => setLang("en")} className={lang === "en" ? "text-[color:var(--sacrvm-gold)]" : "text-white/50"}>EN</button>
              </div>
              <a data-testid="mobile-whatsapp" href={`https://wa.me/${BRAND.whatsappNumber}`} className="link-underline mt-6" target="_blank" rel="noreferrer">WhatsApp</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
